import React, { useState } from 'react';
import { SmsTemplateType, SystemConfig } from '@/types';
import { ChevronRight, Info } from 'lucide-react';
import { VariableList } from './settingsShared';

interface SmsTemplatesSectionProps {
  config: SystemConfig;
  onUpdate: (config: SystemConfig) => void;
}

export const SmsTemplatesSection: React.FC<SmsTemplatesSectionProps> = ({ config, onUpdate }) => {
  const [selectedType, setSelectedType] = useState<SmsTemplateType>('booking_confirmation');
  const smsTemplates = config.templates.smsTemplates || [];
  const template = smsTemplates.find((t) => t.type === selectedType);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-4 gap-8 animate-fade-in">
      <div className="space-y-1 bg-white p-2 rounded-2xl border border-slate-200 shadow-sm h-fit">
        <p className="px-3 py-2 text-[10px] font-black uppercase text-slate-400">Trigger Events</p>
        {smsTemplates.length === 0 ? (
          <p className="text-xs text-slate-400 italic px-3">No templates found.</p>
        ) : (
          smsTemplates.map((t) => (
            <button
              key={t.type}
              onClick={() => setSelectedType(t.type)}
              className={`w-full text-left p-3 rounded-xl transition-all border-2 text-xs font-bold flex items-center justify-between group ${selectedType === t.type ? 'border-teal-500 bg-teal-50 text-teal-800' : 'border-transparent bg-white text-slate-500 hover:bg-slate-50'}`}
            >
              {t.label}
              <ChevronRight
                className={`w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity ${selectedType === t.type ? 'opacity-100' : ''}`}
              />
            </button>
          ))
        )}
      </div>

      <div className="lg:col-span-3 space-y-6">
        {!template ? (
          <div className="p-12 text-center text-slate-400">Select a trigger from the left</div>
        ) : (
          <div className="bg-white p-8 rounded-2xl border border-slate-200 shadow-sm space-y-6">
            <div className="flex justify-between items-center border-b pb-4">
              <h4 className="font-bold text-lg text-slate-800">Edit SMS: {template.label}</h4>
              <VariableList />
            </div>
            <div className="relative">
              <label className="block text-xs font-bold text-slate-500 uppercase mb-2">
                SMS Body (Plain Text)
              </label>
              <textarea
                className="w-full border rounded-xl p-4 bg-slate-50 outline-none focus:ring-2 focus:ring-teal-500 font-mono text-sm leading-relaxed"
                rows={6}
                maxLength={160}
                value={template.body}
                onChange={(e) => {
                  const newTemplates = [...smsTemplates];
                  const idx = newTemplates.findIndex((x) => x.type === selectedType);
                  newTemplates[idx] = { ...template, body: e.target.value };
                  onUpdate({
                    ...config,
                    templates: { ...config.templates, smsTemplates: newTemplates }
                  });
                }}
              />
              <div
                className={`absolute bottom-3 right-3 text-[10px] font-bold px-2 py-0.5 rounded ${template.body.length > 140 ? 'bg-orange-100 text-orange-600' : 'bg-slate-200 text-slate-500'}`}
              >
                {template.body.length} / 160
              </div>
            </div>
            <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 flex gap-3 items-center">
              <Info className="w-5 h-5 text-blue-600" />
              <p className="text-xs text-blue-700">
                SMS variables will be replaced automatically. Ensure content is under 160 characters
                to avoid multiple segment costs.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
